import React, { useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import {
  createColumnHelper, flexRender, getCoreRowModel, getSortedRowModel, SortingState, useReactTable
} from '@tanstack/react-table';

import { Alert } from '../../api';
import { getIconForSortDirection } from '../../components/Library/getIconForSortDirection';
import CenterLoadingSpinner from '../../components/LoadingSpinner/CenterLoadingSpinner';
import { humanizedTimeSpan } from '../../libs/libDate';
import { useAlertsQuery } from './';

interface AlertListProps {
  alerts?: Alert[]
}

const columnHelper = createColumnHelper<Alert>();

interface AlertLinkProps {
  alertId: string;
  name: string;
}

const AlertLink: React.FC<AlertLinkProps> = (props) => {
  const { search } = useLocation();
  return <Link to={`${props.alertId}?alertsQuery=${encodeURIComponent(search)}`} className='overflow-hidden overflow-ellipsis whitespace-nowrap underline'>{props.name}</Link>;
}

const columns = [
  columnHelper.accessor('name', {
    header: () => 'Name',
    cell: info => <AlertLink alertId={info.row.original.id} name={info.getValue()} />
  }),
  columnHelper.accessor('alert_source', {
    header: () => 'Source',
    cell: info => info.getValue()
  }),
  columnHelper.accessor('status', {
    header: () => 'Status',
    cell: info => info.getValue()
  }),
  columnHelper.accessor('alert_time', {
    header: () => 'Alert Time',
    cell: info => {
      const value = info.getValue();
      return value == null ? <i>Null</i> : humanizedTimeSpan(value)
    }
  })
];

const DEFAULT_SORT = [{ desc: true, id: 'alert_time' }];

const getInitialSortState = (search: URLSearchParams): SortingState => {
  try {
    const sort = search.get('sort');
    if (sort != null) {
      const sortState = JSON.parse(decodeURIComponent(sort));
      return sortState;
    }
  } catch {
    console.error('Failed to parse existing sort state. Resetting to default');
  }
  return DEFAULT_SORT;
};

const getString = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    return value;
  }
};

const AlertList: React.FC<AlertListProps> = (props) => {
  const [search, setSearch] = useAlertsQuery();
  const [sorting, setSorting] = useState<SortingState>(getInitialSortState(search));

  const setSortState = (state: React.SetStateAction<SortingState>): void => {
    let newState: SortingState;
    if (typeof state === 'function') {
      newState = state(sorting);
    } else {
      newState = state;
    }

    setSorting(newState);
    setSearch((prev) => {
      const output = new URLSearchParams(prev);

      output.set('sort', JSON.stringify(newState));

      return output;
    });
  };

  const table = useReactTable({
    data: props.alerts ?? [],
    state: {
      sorting
    },
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSortState
  });
  
  if (props.alerts == null) {
    return <CenterLoadingSpinner />;
  }

  return <>
    <table className='w-full max-w-full md:w-[calc(100vw-416px)] mx-8 md:mx-12 overflow-hidden table-fixed'>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className='border-b border-b-background3-dark'>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} className={`py-3 text-left px-2 w-col-${header.column.id} ${header.column.id === 'subject' ? 'table-cell' : 'hidden md:table-cell'}`}>
                    {header.isPlaceholder
                      ? null
                      : <div onClick={header.column.getToggleSortingHandler()} className={`flex ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''}`}>
                        <div className='inline-block'>{flexRender(header.column.columnDef.header, header.getContext())}</div>
                        <div className='inline-block ml-auto'>{getIconForSortDirection(header.column.getIsSorted())}</div>
                      </div>}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <tr key={row.id} className='border-b border-b-background3-dark hover:bg-hover'>
                {row.getVisibleCells().map(cell => {
                  const overflowClasses = 'overflow-hidden overflow-ellipsis';
                  const useOverflow = cell.column.id === 'subject';
                  return (
                    <td key={cell.id} className={`p-2 whitespace-nowrap w-col-${cell.column.id} ${overflowClasses} ${useOverflow ? 'table-cell' : 'hidden md:table-cell'}`} title={getString(cell.getValue())}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            ))}
            {table.getRowModel().rows.length === 0 && <>
              <tr className='border-b border-b-background3-dark'>
                <td colSpan={columns.length} className='p-2'>
                  There are no alerts to show for this account.
                </td>
              </tr>
            </>}
          </tbody>
        </table>
  </>;
};

export default AlertList;